import type { Permission, Role } from '@kortex/shared';

/**
 * What each role is walked through the first time they arrive.
 *
 * Tours are per role because the product is: a cashier never sees procurement
 * and a housekeeper never sees the till, and marching somebody past screens
 * they cannot open teaches them nothing except that the tour is not for them.
 *
 * Steps are skipped rather than shown greyed when the person lacks the
 * permission, so a manager without payroll simply never hears about it.
 */

export interface TourStep {
  id: string;
  title: string;
  body: string;
  /**
   * What to put the spotlight on. A step with no target is a plain card in
   * the middle - right for an opening or a closing word, wrong for anything
   * that is about a particular control.
   */
  target?: string;
  /** Go here before the step is shown. */
  route?: string;
  /**
   * What the person has to do to move on.
   *
   * The point of the whole exercise. "This is the Add Product button" is a
   * label; "Click Add Product to continue" is a lesson, because they end up
   * having done it. `waitFor` is a selector whose appearance means they did.
   */
  action?: {
    /** Shown in place of the Next button. */
    label: string;
    /** Clicking inside this advances. Defaults to the step's own target. */
    click?: string;
    /** Or: this appearing advances, for something that opens a panel. */
    waitFor?: string;
    /** Or: arriving here advances. */
    route?: string;
  };
  /** Hidden from anybody without it. */
  permission?: Permission;
}

export interface Tour {
  id: string;
  title: string;
  /** One line under the title on the opening card. */
  summary: string;
  steps: TourStep[];
}

/** Nav items carry `data-tour="nav:<path>"`, so every screen is targetable. */
const nav = (path: string) => `[data-tour="nav:${path}"]`;

const WELCOME = (who: string, what: string): TourStep => ({
  id: 'welcome',
  title: `Welcome to SARTHI`,
  body: `${who} ${what} This takes about a minute, and you can leave at any point — the 🧭 button at the top right brings it back.`,
});

const FINISH: TourStep = {
  id: 'finish',
  title: 'That is the tour',
  body:
    'Everything you need is in the left-hand menu, and the Need Help button at the bottom answers ' +
    'questions in your own words. Your Getting Started list is on the dashboard.',
};

const OWNER: Tour = {
  id: 'owner',
  title: 'Running your hotel',
  summary: 'The screens you will live in, and the ones you will visit once a month.',
  steps: [
    WELCOME('You own this hotel, so you see everything.', 'Here is the shape of it.'),
    {
      id: 'dashboard',
      title: 'Your morning screen',
      body: 'Today’s takings, covers, occupancy and anything below reorder level — one glance before service.',
      route: '/',
      target: nav('/'),
      permission: 'dashboard:view',
    },
    {
      id: 'checklist',
      title: 'Getting started',
      body: 'This list is worked out from your own data, not ticked off by hand. It is the fastest route to a hotel that can take money.',
      target: '[data-tour="checklist"]',
      route: '/',
      permission: 'dashboard:view',
    },
    {
      id: 'menu',
      title: 'Your menu comes priced at zero',
      body: 'Deliberately: we seeded the dishes, you set what they are worth. Nothing can be billed until you do.',
      route: '/menu',
      target: nav('/menu'),
      permission: 'menu:manage',
    },
    {
      id: 'pos',
      title: 'Billing',
      body: 'Where every sale starts. Let us open it.',
      target: nav('/pos'),
      permission: 'pos:operate',
      action: { label: 'Open POS Billing to continue', click: nav('/pos'), route: '/pos' },
    },
    {
      id: 'ticket',
      title: 'Start a ticket',
      body: 'A ticket is one table, one room or one takeaway. Open one and the menu appears.',
      target: '[data-tour="pos-new-ticket"]',
      permission: 'pos:operate',
      action: { label: 'Click + New ticket to continue', click: '[data-tour="pos-new-ticket"]' },
    },
    {
      id: 'people',
      title: 'Staff and payroll',
      body: 'Attendance, salaries and advances. Everyone who signs in has their own account, so the audit trail names them.',
      route: '/people',
      target: nav('/people'),
      permission: 'hr:view',
    },
    {
      id: 'reports',
      title: 'What the month did',
      body: 'Day Book reconciles a single day; Profit & Loss and GST Filing are what your accountant asks for.',
      route: '/reports',
      target: nav('/reports'),
      permission: 'reports:view',
    },
    {
      id: 'settings',
      title: 'Taxes, charges and rooms',
      body: 'GST slabs arrive set correctly for India. Your GSTIN, service charge and room types live here.',
      route: '/settings',
      target: nav('/settings'),
      permission: 'settings:manage',
    },
    FINISH,
  ],
};

const MANAGER: Tour = {
  id: 'manager',
  title: 'Running the floor',
  summary: 'Service, stock and the people on shift.',
  steps: [
    WELCOME('You run the floor.', 'These are the screens that keep service moving.'),
    {
      id: 'pos',
      title: 'Billing',
      body: 'Every sale starts here. Open it and a ticket begins.',
      target: nav('/pos'),
      permission: 'pos:operate',
      action: { label: 'Open POS Billing to continue', click: nav('/pos'), route: '/pos' },
    },
    {
      id: 'kitchen',
      title: 'Kitchen display',
      body: 'What the kitchen is cooking, in the order it arrived. Nothing is lost between billing and the kitchen pass.',
      route: '/kitchen',
      target: nav('/kitchen'),
      permission: 'kds:view',
    },
    {
      id: 'inventory',
      title: 'What you are running out of',
      body: 'Stock on hand against reorder level. The shopping list writes itself.',
      route: '/inventory',
      target: nav('/inventory'),
      permission: 'inventory:view',
    },
    {
      id: 'procurement',
      title: 'Ordering it',
      body: 'Raise a purchase order and it is emailed to the supplier the moment it is approved.',
      route: '/procurement',
      target: nav('/procurement'),
      permission: 'procurement:view',
    },
    {
      id: 'daybook',
      title: 'Closing the day',
      body: 'Sales, expenses and what should be in the drawer, reconciled before you go home.',
      route: '/day-book',
      target: nav('/day-book'),
      permission: 'daybook:view',
    },
    FINISH,
  ],
};

const CASHIER: Tour = {
  id: 'cashier',
  title: 'Taking an order',
  summary: 'From an empty table to a paid bill.',
  steps: [
    WELCOME('You handle billing.', 'Let us take one order together.'),
    {
      id: 'pos',
      title: 'Open billing',
      body: 'Everything you do lives on this one screen.',
      target: nav('/pos'),
      permission: 'pos:operate',
      action: { label: 'Open POS Billing to continue', click: nav('/pos'), route: '/pos' },
    },
    {
      id: 'ticket',
      title: 'Start a ticket',
      body: 'One ticket per table, room or takeaway. Open one now.',
      target: '[data-tour="pos-new-ticket"]',
      permission: 'pos:operate',
      action: { label: 'Click + New ticket to continue', click: '[data-tour="pos-new-ticket"]' },
    },
    {
      id: 'items',
      title: 'Add what they ordered',
      body: 'Tap a dish to put it on the ticket. Tap it twice for two. The total keeps itself up to date.',
      target: '[data-tour="pos-menu"]',
      permission: 'pos:operate',
    },
    {
      id: 'bill',
      title: 'Take the money',
      body: 'Bill closes the ticket, prints, and books the sale. Cash, UPI or card — split across them if the table does.',
      target: '[data-tour="pos-bill"]',
      permission: 'pos:bill',
    },
    {
      id: 'invoices',
      title: 'Finding it again',
      body: 'Every bill you have raised, reprintable, with what was paid and how.',
      route: '/invoices',
      target: nav('/invoices'),
      permission: 'invoice:view',
    },
    FINISH,
  ],
};

const FRONT_DESK: Tour = {
  id: 'front_desk',
  title: 'The front desk',
  summary: 'Arrivals, departures and the room board.',
  steps: [
    WELCOME('You are on the desk.', 'Here is a day at it.'),
    {
      id: 'frontdesk',
      title: 'The room board',
      body: 'Who is in, who is leaving, what is clean. Check-in and check-out both start here.',
      route: '/front-desk',
      target: nav('/front-desk'),
      permission: 'rooms:view',
      action: { label: 'Open Front Desk to continue', click: nav('/front-desk'), route: '/front-desk' },
    },
    {
      id: 'reservations',
      title: 'What is booked',
      body: 'Today’s arrivals and everything ahead. A reservation becomes a stay at check-in.',
      route: '/reservations',
      target: nav('/reservations'),
      permission: 'reservation:view',
    },
    {
      id: 'housekeeping',
      title: 'Is the room ready?',
      body: 'Housekeeping marks rooms clean here, and the board above updates as they do.',
      route: '/housekeeping',
      target: nav('/housekeeping'),
      permission: 'housekeeping:view',
    },
    FINISH,
  ],
};

const STOREKEEPER: Tour = {
  id: 'storekeeper',
  title: 'The store',
  summary: 'What you have, what you need, and what arrived.',
  steps: [
    WELCOME('You keep the store.', 'Three screens do all of it.'),
    {
      id: 'inventory',
      title: 'What is on the shelf',
      body: 'Stock on hand against reorder level, with anything short flagged.',
      route: '/inventory',
      target: nav('/inventory'),
      permission: 'inventory:view',
      action: { label: 'Open Inventory to continue', click: nav('/inventory'), route: '/inventory' },
    },
    {
      id: 'procurement',
      title: 'Ordering more',
      body: 'Raise a purchase order from the reorder list. Once approved it is emailed to the supplier.',
      route: '/procurement',
      target: nav('/procurement'),
      permission: 'procurement:view',
    },
    {
      id: 'receive',
      title: 'Booking a delivery in',
      body: 'Receive against the order. Stock goes up, costs re-average, and short deliveries stay open.',
      route: '/procurement',
      target: nav('/procurement'),
      permission: 'procurement:view',
    },
    FINISH,
  ],
};

const KITCHEN: Tour = {
  id: 'kitchen',
  title: 'The pass',
  summary: 'One screen, in order of arrival.',
  steps: [
    WELCOME('You are on the pass.', 'There is only one screen you need.'),
    {
      id: 'kds',
      title: 'What is on',
      body: 'Tickets arrive as they are rung up, oldest first. Mark an item started and the floor can see it.',
      route: '/kitchen',
      target: nav('/kitchen'),
      permission: 'kds:view',
      action: { label: 'Open Kitchen Display to continue', click: nav('/kitchen'), route: '/kitchen' },
    },
    FINISH,
  ],
};

const HOUSEKEEPING: Tour = {
  id: 'housekeeping',
  title: 'Housekeeping',
  summary: 'Your rooms for today.',
  steps: [
    WELCOME('You look after the rooms.', 'Here is your list.'),
    {
      id: 'tasks',
      title: 'Today’s rooms',
      body: 'What needs cleaning, in priority order. Mark one done and the front desk can let the next guest in.',
      route: '/housekeeping',
      target: nav('/housekeeping'),
      permission: 'housekeeping:view',
      action: { label: 'Open Housekeeping to continue', click: nav('/housekeeping'), route: '/housekeeping' },
    },
    FINISH,
  ],
};

const ACCOUNTANT: Tour = {
  id: 'accountant',
  title: 'The books',
  summary: 'Reconciliation, filing and what the month made.',
  steps: [
    WELCOME('You keep the books.', 'Four screens, in the order you will want them.'),
    {
      id: 'daybook',
      title: 'A single day, reconciled',
      body: 'Sales, expenses and the drawer, for any date you pick.',
      route: '/day-book',
      target: nav('/day-book'),
      permission: 'daybook:view',
      action: { label: 'Open Day Book to continue', click: nav('/day-book'), route: '/day-book' },
    },
    {
      id: 'pnl',
      title: 'Profit and loss',
      body: 'Revenue against food cost, payroll, procurement and expenses.',
      route: '/pnl',
      target: nav('/pnl'),
      permission: 'pnl:view',
    },
    {
      id: 'gst',
      title: 'GST filing',
      body: 'GSTR-1 exports built from the invoices as issued, not recalculated.',
      route: '/gst',
      target: nav('/gst'),
      permission: 'gst:export',
    },
    {
      id: 'expenses',
      title: 'Money going out',
      body: 'Recorded against a category so the profit and loss adds up.',
      route: '/expenses',
      target: nav('/expenses'),
      permission: 'expense:view',
    },
    FINISH,
  ],
};

const BY_ROLE: Record<Role, Tour> = {
  OWNER: OWNER,
  MANAGER: MANAGER,
  CASHIER: CASHIER,
  FRONT_DESK: FRONT_DESK,
  STOREKEEPER: STOREKEEPER,
  KITCHEN: KITCHEN,
  HOUSEKEEPING: HOUSEKEEPING,
  ACCOUNTANT: ACCOUNTANT,
};

/** The tour for this role, with steps they cannot act on removed. */
export function tourFor(role: Role | null, permissions: Permission[]): Tour | null {
  if (!role) return null;
  const tour = BY_ROLE[role];
  if (!tour) return null;

  const steps = tour.steps.filter(
    (step) => !step.permission || permissions.includes(step.permission),
  );
  // A tour of nothing but hello and goodbye is not worth starting.
  return steps.length > 2 ? { ...tour, steps } : null;
}
